import React, { useMemo } from "react";
import {
  Appointment,
  AppointmentStatus,
} from "../../../interfaces/services/Appointment";
import { parseFixedISOStringToDate } from "../../../utils/dateUtils";
import { Badge, HoverCard, Text } from "@mantine/core";
import { useSetAtom } from "jotai";
import {
  calendarRangeAtom,
  calendarStartDateAtom,
  getStatusColor,
} from "../../../pages/profile/Appointment/AppointmentsCalendarPage";

type TimeStripMonthlyProps = {
  monthDate: Date;
  appointments: Appointment[];
  className?: string;
};

// ====== Yardımcılar ======

function getMondayIndex(jsDay: number) {
  return (jsDay + 6) % 7;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}
function endOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, 0, 0, 0, 0);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 0, 0, 0, 0);
}
function daysInMonthOf(d: Date): number {
  const y = d.getFullYear();
  const m = d.getMonth();
  return new Date(y, m + 1, 0).getDate();
}
function fmtKeyYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getMonthRange(d: Date) {
  const y = d.getFullYear();
  const m = d.getMonth();
  const first = new Date(y, m, 1, 0, 0, 0, 0);
  const last = new Date(y, m, daysInMonthOf(d), 0, 0, 0, 0);
  return { first, last };
}

function overlapsDay(apptStart: Date, apptEnd: Date, dayStart: Date): boolean {
  const day0 = startOfLocalDay(dayStart).getTime();
  const day1 = endOfLocalDay(dayStart).getTime();
  const a0 = apptStart.getTime();
  const a1 = apptEnd.getTime();
  return a0 < day1 && a1 > day0;
}

// Durum sırası (gösterimde bu sırayla)
const STATUS_ORDER: AppointmentStatus[] = [
  "Approved",
  "Pending",
  "Completed",
  "Canceled",
  "NoShow",
];

type DayStatusCounts = Partial<Record<AppointmentStatus, number>>;

function statusLabel(status: AppointmentStatus): string {
  switch (status) {
    case "Approved":
      return "onaylı";
    case "Pending":
      return "bekliyor";
    case "Completed":
      return "tamamlandı";
    case "Canceled":
      return "iptal";
    case "NoShow":
      return "gelmedi";
    default:
      return status;
  }
}

function computeDailyStatusCounts(
  monthDate: Date,
  appointments: Appointment[],
): Map<string, DayStatusCounts> {
  const { first, last } = getMonthRange(monthDate);
  const monthStart = startOfLocalDay(first);
  const monthEndExclusive = endOfLocalDay(last);

  const totalDays = daysInMonthOf(monthDate);
  const counts = new Map<string, DayStatusCounts>();
  for (let i = 0; i < totalDays; i++)
    counts.set(fmtKeyYYYYMMDD(addDays(first, i)), {});

  for (const a of appointments) {
    const apptStart = parseFixedISOStringToDate(
      a.appointmentTime.appointmentStartDate,
    );
    const apptEnd = parseFixedISOStringToDate(
      a.appointmentTime.appointmentEndDate,
    );

    if (apptEnd <= monthStart || apptStart >= monthEndExclusive) continue;

    const spanStart = startOfLocalDay(
      apptStart < monthStart ? monthStart : apptStart,
    );
    const spanEndExclusive = endOfLocalDay(apptEnd > last ? last : apptEnd);

    for (
      let cur = spanStart;
      cur.getTime() < spanEndExclusive.getTime();
      cur = addDays(cur, 1)
    ) {
      if (overlapsDay(apptStart, apptEnd, cur)) {
        const key = fmtKeyYYYYMMDD(cur);
        const dayCounts = counts.get(key);
        if (dayCounts !== undefined) {
          dayCounts[a.status] = (dayCounts[a.status] || 0) + 1;
        }
      }
    }
  }
  return counts;
}

// Türkçe kısa gün başlıkları (Pzt–Paz)
const WEEK_HEADERS_TR = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cts", "Paz"];

const OUTSIDE_MONTH_HATCH_STYLE: React.CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(45deg, rgba(120,120,120,0.14) 0, rgba(120,120,120,0.14) 6px, transparent 6px, transparent 12px)",
  backgroundColor: "rgba(120,120,120,0.06)",
};

export default function TimeStripMonthly({
  monthDate,
  appointments,
  className = "",
}: TimeStripMonthlyProps) {
  const setStartDate = useSetAtom(calendarStartDateAtom);
  const setRange = useSetAtom(calendarRangeAtom);

  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const { first } = getMonthRange(monthDate);
  const totalDays = daysInMonthOf(monthDate);

  const firstDayMondayIndex = getMondayIndex(first.getDay());
  const leadingBlanks = firstDayMondayIndex;
  const totalCells = leadingBlanks + totalDays;
  const rows = Math.ceil(totalCells / 7);

  const cells: (Date | null)[] = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(new Date(y, m, d));
  while (cells.length < rows * 7) cells.push(null);

  const statusCounts = useMemo(
    () => computeDailyStatusCounts(monthDate, appointments),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthDate.getFullYear(), monthDate.getMonth(), appointments],
  );

  const monthTitle = first.toLocaleDateString("tr-TR", {
    month: "long",
    year: "numeric",
  });

  return (
    <div
      className={[
        "min-w-[800px] overflow-hidden rounded-2xl border border-background-lightAlt2 dark:border-background-darkAlt2 bg-background-light dark:bg-background-dark shadow-sm w-full",
        className,
      ].join(" ")}
    >
      {/* Başlık */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-background-lightAlt2 dark:border-background-darkAlt2">
        <div className="text-base font-semibold text-gray-900 dark:text-gray-100 capitalize">
          {monthTitle}
        </div>
      </div>

      {/* Hafta başlıkları */}
      <div className="grid grid-cols-7 text-[11px] uppercase tracking-wide text-gray-500">
        {WEEK_HEADERS_TR.map((h, i) => (
          <div
            key={`wh-${i}`}
            className="px-3 py-2 border-b border-background-lightAlt2 dark:border-background-darkAlt2"
          >
            {h}
          </div>
        ))}
      </div>

      {/* Takvim gövdesi */}
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          if (cell === null) {
            return (
              <HoverCard key={`cell-${idx}`} width={280} shadow="md">
                <HoverCard.Target>
                  <div
                    className="h-32 border-r border-b last:border-r-0 border-background-lightAlt2 dark:border-background-darkAlt2"
                    style={OUTSIDE_MONTH_HATCH_STYLE}
                  />
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text size="sm">Bu aya dahil değil</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            );
          }

          const key = fmtKeyYYYYMMDD(cell);
          const dayCounts = statusCounts.get(key) ?? {};
          const totalCount = STATUS_ORDER.reduce(
            (sum, s) => sum + (dayCounts[s] ?? 0),
            0,
          );
          const isToday = fmtKeyYYYYMMDD(cell) === fmtKeyYYYYMMDD(new Date());

          return (
            <div
              key={`cell-${idx}`}
              className="h-32 px-2 py-2 border-r border-b last:border-r-0 border-background-lightAlt2 dark:border-background-darkAlt2 bg-background-light dark:bg-background-dark cursor-pointer hover:bg-background-lightAlt1 dark:hover:bg-background-darkAlt1 overflow-hidden"
              onClick={() => {
                setStartDate(cell);
                setRange("daily");
              }}
            >
              {/* Tarih numarası sağ üstte */}
              <div className="flex justify-end mb-1">
                <Badge
                  className="text-xs"
                  color={isToday ? "blue" : "gray"}
                  variant={isToday ? "filled" : "light"}
                >
                  {cell.getDate()}
                </Badge>
              </div>

              {/* Durum bazlı badge'ler */}
              {totalCount > 0 && (
                <div className="flex flex-wrap gap-0.5">
                  {STATUS_ORDER.map((status) => {
                    const n = dayCounts[status] ?? 0;
                    if (!n) return null;
                    return (
                      <div
                        key={status}
                        className="flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold text-white leading-none"
                        style={{ backgroundColor: getStatusColor(status) }}
                      >
                        <span>{n}</span>
                        <span className="opacity-90">
                          {statusLabel(status)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
